import { Store } from '@tanstack/store'
import { useStore } from '@tanstack/react-store'
import type {
  AssistantGenerationPhase,
  AssistantGenerationToolCategory,
  ChatMessageAgent,
} from '@laplace/contracts'
import type { BootstrapData } from '@/features/bootstrap/types/bootstrap'
import type { ChatMessageArtifact } from '@laplace/contracts'
import type { ChatMessage, ChatSummary } from '@/features/chats/types/chat'
import {
  readFailedGenerations,
  writeFailedGenerations,
} from './failed-generation-storage'
import type { FailedGeneration } from './failed-generation-storage'

export type { FailedGeneration } from './failed-generation-storage'

export interface GenerationStatus {
  phase: AssistantGenerationPhase
  tool?: AssistantGenerationToolCategory
}

// A completed assistant turn's thinking trace, kept in-session so the collapsed
// panel stays available under the answer after generation finishes. Keyed by the
// persisted assistant message id. Only the high-level, leak-safe action timeline
// is kept — raw model reasoning is never surfaced in the UI.
export interface MessageTrace {
  steps: Array<GenerationStatus>
}

export interface ChatShellLoadingState {
  bootstrap: boolean
  chatsByWorkspaceId: Record<string, boolean>
  messagesByChatId: Record<string, boolean>
}

export interface ChatShellErrorState {
  bootstrap: string | null
  chatsByWorkspaceId: Record<string, string | null>
  messagesByChatId: Record<string, string | null>
}

export interface ChatShellState {
  bootstrap: BootstrapData | null
  currentWorkspaceId: string | null
  currentChatId: string | null
  abortControllerByChatId: Record<string, AbortController>
  chatsByWorkspaceId: Record<string, Array<ChatSummary>>
  messagesByChatId: Record<string, Array<ChatMessage>>
  draftsByChatContextId: Record<string, string>
  selectedAgentIdByChatContextId: Record<string, string | null | undefined>
  scrollTopByChatContextId: Record<string, number | undefined>
  generationInFlightByChatId: Record<string, boolean>
  assistantTypingByChatId: Record<string, boolean>
  streamingAssistantMessageByChatId: Record<string, string>
  streamingArtifactsByChatId: Record<string, Array<ChatMessageArtifact>>
  generationStatusByChatId: Record<string, GenerationStatus | null>
  generationStepsByChatId: Record<string, Array<GenerationStatus>>
  thinkingCollapsedByChatId: Record<string, boolean>
  traceByMessageId: Record<string, MessageTrace>
  generationAgentByChatId: Record<string, ChatMessageAgent | null>
  failedGenerationByChatId: Record<string, FailedGeneration | undefined>
  loading: ChatShellLoadingState
  errors: ChatShellErrorState
}

export const chatShellStore = new Store<ChatShellState>({
  bootstrap: null,
  currentWorkspaceId: null,
  currentChatId: null,
  abortControllerByChatId: {},
  chatsByWorkspaceId: {},
  messagesByChatId: {},
  draftsByChatContextId: {},
  selectedAgentIdByChatContextId: {},
  scrollTopByChatContextId: {},
  generationInFlightByChatId: {},
  assistantTypingByChatId: {},
  streamingAssistantMessageByChatId: {},
  streamingArtifactsByChatId: {},
  generationStatusByChatId: {},
  generationStepsByChatId: {},
  thinkingCollapsedByChatId: {},
  traceByMessageId: {},
  generationAgentByChatId: {},
  failedGenerationByChatId: readFailedGenerations(),
  loading: {
    bootstrap: false,
    chatsByWorkspaceId: {},
    messagesByChatId: {},
  },
  errors: {
    bootstrap: null,
    chatsByWorkspaceId: {},
    messagesByChatId: {},
  },
})

export const chatShellActions = {
  setBootstrap(bootstrap: BootstrapData | null) {
    chatShellStore.setState((state) => ({
      ...state,
      bootstrap,
    }))
  },
  setCurrentWorkspaceId(workspaceId: string | null) {
    chatShellStore.setState((state) => ({
      ...state,
      currentWorkspaceId: workspaceId,
      currentChatId:
        workspaceId === null || state.currentWorkspaceId === workspaceId
          ? state.currentChatId
          : null,
    }))
  },
  setCurrentChatId(chatId: string | null) {
    chatShellStore.setState((state) => ({
      ...state,
      currentChatId: chatId,
    }))
  },
  setAbortController(chatId: string, controller: AbortController | null) {
    chatShellStore.setState((state) => {
      const nextControllers = { ...state.abortControllerByChatId }
      if (controller === null) {
        delete nextControllers[chatId]
      } else {
        nextControllers[chatId] = controller
      }
      return {
        ...state,
        abortControllerByChatId: nextControllers,
      }
    })
  },
  setWorkspaceChats(workspaceId: string, chats: Array<ChatSummary>) {
    chatShellStore.setState((state) => ({
      ...state,
      chatsByWorkspaceId: {
        ...state.chatsByWorkspaceId,
        [workspaceId]: chats,
      },
    }))
  },
  setChatMessages(chatId: string, messages: Array<ChatMessage>) {
    chatShellStore.setState((state) => ({
      ...state,
      messagesByChatId: {
        ...state.messagesByChatId,
        [chatId]: messages,
      },
    }))
  },
  setDraft(chatContextId: string, value: string) {
    chatShellStore.setState((state) => ({
      ...state,
      draftsByChatContextId: {
        ...state.draftsByChatContextId,
        [chatContextId]: value,
      },
    }))
  },
  clearDraft(chatContextId: string) {
    chatShellStore.setState((state) => {
      const nextDrafts = {
        ...state.draftsByChatContextId,
      }
      delete nextDrafts[chatContextId]

      return {
        ...state,
        draftsByChatContextId: nextDrafts,
      }
    })
  },
  setSelectedAgent(chatContextId: string, agentId: string | null) {
    chatShellStore.setState((state) => {
      const nextSelectedAgents = {
        ...state.selectedAgentIdByChatContextId,
      }
      nextSelectedAgents[chatContextId] = agentId

      return {
        ...state,
        selectedAgentIdByChatContextId: nextSelectedAgents,
      }
    })
  },
  moveSelectedAgent(sourceContextId: string, targetContextId: string) {
    chatShellStore.setState((state) => {
      const nextSelectedAgents = {
        ...state.selectedAgentIdByChatContextId,
      }
      const selectedAgentId = nextSelectedAgents[sourceContextId]
      const hasSelection = Object.prototype.hasOwnProperty.call(
        nextSelectedAgents,
        sourceContextId,
      )

      delete nextSelectedAgents[sourceContextId]
      if (hasSelection) {
        nextSelectedAgents[targetContextId] = selectedAgentId
      }

      return {
        ...state,
        selectedAgentIdByChatContextId: nextSelectedAgents,
      }
    })
  },
  setScrollTop(chatContextId: string, scrollTop: number) {
    chatShellStore.setState((state) => ({
      ...state,
      scrollTopByChatContextId: {
        ...state.scrollTopByChatContextId,
        [chatContextId]: scrollTop,
      },
    }))
  },
  setGenerationInFlight(chatId: string, isInFlight: boolean) {
    chatShellStore.setState((state) => ({
      ...state,
      generationInFlightByChatId: {
        ...state.generationInFlightByChatId,
        [chatId]: isInFlight,
      },
    }))
  },
  setAssistantTyping(chatId: string, isTyping: boolean) {
    chatShellStore.setState((state) => ({
      ...state,
      assistantTypingByChatId: {
        ...state.assistantTypingByChatId,
        [chatId]: isTyping,
      },
    }))
  },
  setStreamingAssistantMessage(chatId: string, content: string | null) {
    chatShellStore.setState((state) => {
      const nextStreaming = {
        ...state.streamingAssistantMessageByChatId,
      }

      if (content === null) {
        delete nextStreaming[chatId]
      } else {
        nextStreaming[chatId] = content
      }

      return {
        ...state,
        streamingAssistantMessageByChatId: nextStreaming,
      }
    })
  },
  appendStreamingArtifact(chatId: string, artifact: ChatMessageArtifact) {
    chatShellStore.setState((state) => ({
      ...state,
      streamingArtifactsByChatId: {
        ...state.streamingArtifactsByChatId,
        [chatId]: [
          ...(state.streamingArtifactsByChatId[chatId] ?? []),
          artifact,
        ],
      },
    }))
  },
  clearStreamingArtifacts(chatId: string) {
    chatShellStore.setState((state) => {
      const nextArtifacts = { ...state.streamingArtifactsByChatId }
      delete nextArtifacts[chatId]
      return {
        ...state,
        streamingArtifactsByChatId: nextArtifacts,
      }
    })
  },
  setGenerationStatus(chatId: string, status: GenerationStatus | null) {
    chatShellStore.setState((state) => {
      const nextStatus = {
        ...state.generationStatusByChatId,
      }

      if (status === null) {
        delete nextStatus[chatId]
      } else {
        nextStatus[chatId] = status
      }

      return {
        ...state,
        generationStatusByChatId: nextStatus,
      }
    })
  },
  pushGenerationStep(chatId: string, status: GenerationStatus) {
    chatShellStore.setState((state) => {
      const current = state.generationStepsByChatId[chatId] ?? []
      const last = current.length > 0 ? current[current.length - 1] : undefined
      // Collapse consecutive duplicates so the timeline reads as distinct
      // actions, not one phase repeated per stream chunk.
      if (last && last.phase === status.phase && last.tool === status.tool) {
        return state
      }

      return {
        ...state,
        generationStepsByChatId: {
          ...state.generationStepsByChatId,
          [chatId]: [...current, status],
        },
      }
    })
  },
  commitTraceForMessage(chatId: string, messageId: string) {
    chatShellStore.setState((state) => {
      const steps = state.generationStepsByChatId[chatId] ?? []

      if (steps.length === 0) {
        return state
      }

      return {
        ...state,
        traceByMessageId: {
          ...state.traceByMessageId,
          [messageId]: { steps },
        },
      }
    })
  },
  setThinkingCollapsed(chatId: string, collapsed: boolean) {
    chatShellStore.setState((state) => ({
      ...state,
      thinkingCollapsedByChatId: {
        ...state.thinkingCollapsedByChatId,
        [chatId]: collapsed,
      },
    }))
  },
  setGenerationAgent(chatId: string, agent: ChatMessageAgent | null) {
    chatShellStore.setState((state) => ({
      ...state,
      generationAgentByChatId: {
        ...state.generationAgentByChatId,
        [chatId]: agent,
      },
    }))
  },
  clearGenerationUiState(chatId: string) {
    chatShellStore.setState((state) => {
      const nextStreaming = { ...state.streamingAssistantMessageByChatId }
      const nextArtifacts = { ...state.streamingArtifactsByChatId }
      const nextStatus = { ...state.generationStatusByChatId }
      const nextSteps = { ...state.generationStepsByChatId }
      const nextCollapsed = { ...state.thinkingCollapsedByChatId }
      const nextAgents = { ...state.generationAgentByChatId }
      const nextControllers = { ...state.abortControllerByChatId }

      delete nextStreaming[chatId]
      delete nextArtifacts[chatId]
      delete nextStatus[chatId]
      delete nextSteps[chatId]
      delete nextCollapsed[chatId]
      delete nextAgents[chatId]
      delete nextControllers[chatId]

      return {
        ...state,
        streamingAssistantMessageByChatId: nextStreaming,
        streamingArtifactsByChatId: nextArtifacts,
        generationStatusByChatId: nextStatus,
        generationStepsByChatId: nextSteps,
        thinkingCollapsedByChatId: nextCollapsed,
        generationAgentByChatId: nextAgents,
        abortControllerByChatId: nextControllers,
      }
    })
  },
  setFailedGeneration(chatId: string, failed: FailedGeneration | null) {
    chatShellStore.setState((state) => {
      const nextFailed = {
        ...state.failedGenerationByChatId,
      }

      if (failed === null) {
        delete nextFailed[chatId]
      } else {
        nextFailed[chatId] = failed
      }

      writeFailedGenerations(nextFailed)

      return {
        ...state,
        failedGenerationByChatId: nextFailed,
      }
    })
  },
  setBootstrapLoading(isLoading: boolean) {
    chatShellStore.setState((state) => ({
      ...state,
      loading: {
        ...state.loading,
        bootstrap: isLoading,
      },
    }))
  },
  setWorkspaceChatsLoading(workspaceId: string, isLoading: boolean) {
    chatShellStore.setState((state) => ({
      ...state,
      loading: {
        ...state.loading,
        chatsByWorkspaceId: {
          ...state.loading.chatsByWorkspaceId,
          [workspaceId]: isLoading,
        },
      },
    }))
  },
  setChatMessagesLoading(chatId: string, isLoading: boolean) {
    chatShellStore.setState((state) => ({
      ...state,
      loading: {
        ...state.loading,
        messagesByChatId: {
          ...state.loading.messagesByChatId,
          [chatId]: isLoading,
        },
      },
    }))
  },
  setBootstrapError(error: string | null) {
    chatShellStore.setState((state) => ({
      ...state,
      errors: {
        ...state.errors,
        bootstrap: error,
      },
    }))
  },
  setWorkspaceChatsError(workspaceId: string, error: string | null) {
    chatShellStore.setState((state) => ({
      ...state,
      errors: {
        ...state.errors,
        chatsByWorkspaceId: {
          ...state.errors.chatsByWorkspaceId,
          [workspaceId]: error,
        },
      },
    }))
  },
  setChatMessagesError(chatId: string, error: string | null) {
    chatShellStore.setState((state) => ({
      ...state,
      errors: {
        ...state.errors,
        messagesByChatId: {
          ...state.errors.messagesByChatId,
          [chatId]: error,
        },
      },
    }))
  },
}

export function useChatShellStore() {
  const state = useStore(chatShellStore)
  return {
    ...state,
    ...chatShellActions,
  }
}
